import type { ReactElement } from 'react';
import { BackupCompatibility, type BackupDto } from '@gr/core/platform/operations/backups';
import { useT } from '../../../../app/di';
import { formatDateTime, formatFileSize, formatNumber } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button, IconButton, LinkButton } from '../../../../ui-kit/Button/Button';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import styles from './BackupList.module.scss';

export interface BackupListProps {
  backups: readonly BackupDto[];
  downloadUrl(name: string): string;
  /** OPS-UC-8.CC-1: идёт создание или восстановление — восстановить нельзя. */
  busy: boolean;
  pendingName?: string | null;
  onRestore(backup: BackupDto): void;
  onDelete(backup: BackupDto): void;
}

const COMPATIBILITY_TONES: Record<BackupCompatibility, 'success' | 'accent' | 'danger' | 'neutral'> = {
  [BackupCompatibility.Same]: 'success',
  [BackupCompatibility.Older]: 'accent',
  [BackupCompatibility.Newer]: 'danger',
  [BackupCompatibility.Invalid]: 'neutral',
};

/** OPS-UC-9, OPS-UC-9.CC-2, OPS-UC-11.CC-2: копии — откуда, что внутри, подходит ли версии сайта. */
export function BackupList({ backups, downloadUrl, busy, pendingName, onRestore, onDelete }: BackupListProps): ReactElement {
  const { t, tp, locale } = useT();
  const number = (value: number): string => formatNumber(value, locale);
  const restorable = (backup: BackupDto): boolean => backup.compatibility === BackupCompatibility.Same || backup.compatibility === BackupCompatibility.Older;
  return (
    <DataTable<BackupDto>
      label={t('operations.backups.table')}
      rows={backups}
      rowKey={(backup) => backup.name}
      empty={<EmptyState title={t('operations.backups.empty')} text={t('operations.backups.emptyText')} />}
      columns={[
        {
          key: 'name',
          header: t('operations.backups.columns.name'),
          primary: true,
          cell: (backup) => (
            <span className={styles.name}>
              <span className={styles.file}>{backup.name}</span>
              <Badge tone="neutral">{t(`operations.backups.origins.${backup.origin}`)}</Badge>
            </span>
          ),
        },
        {
          key: 'added',
          header: t('operations.backups.columns.added'),
          cell: (backup) => {
            const by = backup.summary?.createdBy ?? backup.addedBy?.displayName ?? null;
            return (
              <span className={styles.cell}>
                <span>{formatDateTime(backup.summary?.createdAt ?? backup.addedAt, locale)}</span>
                {by ? <span className="u-muted u-small">{t('operations.backups.by', { name: by })}</span> : null}
              </span>
            );
          },
        },
        { key: 'size', header: t('operations.backups.columns.size'), align: 'end', cell: (backup) => formatFileSize(backup.size, locale) },
        {
          key: 'contents',
          header: t('operations.backups.columns.contents'),
          cell: (backup) =>
            backup.summary ? (
              <span className={styles.cell}>
                <span>
                  {t('operations.backups.summary', { users: number(backup.summary.users), blogs: number(backup.summary.blogs), posts: number(backup.summary.posts), media: number(backup.summary.media) })}
                </span>
                <span className="u-muted u-small">
                  {t('operations.backups.files', { files: number(backup.summary.files), bytes: formatFileSize(backup.summary.filesBytes, locale) })}
                  {backup.summary.missingFiles > 0 ? `, ${tp('operations.backups.missing', backup.summary.missingFiles, { n: backup.summary.missingFiles })}` : ''}
                </span>
              </span>
            ) : (
              <span className="u-muted">{t('operations.backups.invalid')}</span>
            ),
        },
        {
          key: 'version',
          header: t('operations.backups.columns.version'),
          cell: (backup) => (
            <span className={styles.cell}>
              {backup.summary ? <span>{backup.summary.version}</span> : null}
              <Badge tone={COMPATIBILITY_TONES[backup.compatibility]}>{t(`operations.backups.compatibility.${backup.compatibility}`)}</Badge>
            </span>
          ),
        },
        {
          key: 'actions',
          header: t('operations.backups.columns.actions'),
          actions: true,
          cell: (backup) => (
            <span className={styles.actions}>
              <LinkButton href={downloadUrl(backup.name)} download={backup.name} size="sm" icon="download">
                {t('operations.backups.download')}
              </LinkButton>
              <Button size="sm" disabled={busy || !restorable(backup)} loading={pendingName === backup.name} onClick={() => onRestore(backup)}>
                {t('operations.backups.restore')}
              </Button>
              <IconButton icon="trash" size="sm" label={`${t('operations.backups.delete')}: ${backup.name}`} onClick={() => onDelete(backup)} />
            </span>
          ),
        },
      ]}
    />
  );
}
